import {
  CButton,
  CCol,
  CDropdown,
  CDropdownDivider,
  CDropdownMenu,
  CDropdownToggle,
  CForm,
  CFormGroup,
  CInput,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CSelect,
  CSwitch,
} from "@coreui/react";
import { freeSet } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import { useEffect, useRef, useState } from "react";
import {
  downloadAutomationConfigJson,
  useGetTestRunByAutomationConfigTemplateId,
} from "./services/AutomationListServices";
import { NucToast } from "src/reusable/components/Toast";
import { extractErrorDetail } from "src/views/products/parts-ai/utils/errorDetail";

import { Add as AddIcon } from "@mui/icons-material";
import { Box, Button, TextField } from "@mui/material";
import { TemplatePageHeader, TemplateTabs } from "./shared/TemplateNav";
import PropTypes from "prop-types";
import { useDebounce } from "src/helpers/helperFunctions";
import { areWhereFiltersValid } from "src/reusable/Functions";
import { ListSelect } from "src/reusable";
import { RecordFilter } from "src/reusable/components/RecordFilter";
import { initialWhereFilterValue } from "src/reusable/WhereFilter";
import { useDatasetFields } from "src/views/datasets/services/DatasetServices";
import {
  CreateAutomationConfigModal,
  ImportAutomationConfigModal,
} from "src/views/forms/AutomationConfigModals";
import { useModelVersionDatasets } from "src/views/models/ModelService";

export const IncrementingInput = (props) => {
  /**
   * Component that displays a percent value, and allows a user to change
   * the value by 1% increments via a + and - button
   */
  const [value, setValue] = useState(props?.initValue);

  /** Everytime useState value changes it rerenders component and calls
   * useDebounce with the new useState value
   */
  const debounceValue = useDebounce(value);

  const minValue = 0.0;
  const maxValue = 1.0;
  const incrementAmount = 0.01;

  const handleChange = (rawValue) => {
    let newValue = parseFloat(rawValue);

    if (isNaN(newValue)) {
      newValue = minValue;
    } else if (newValue > maxValue) {
      newValue = maxValue;
    } else if (newValue < minValue) {
      newValue = minValue;
    }

    if (newValue !== value) {
      setValue(newValue);
      props.onChange(newValue);
    }
  };

  useEffect(() => {
    // If props changes, reset the value here as well
    setValue(props?.initValue);
  }, [props?.initValue]);

  /** After onMount, if debounce value changes this useEffect
   * is called and it calls handleChange
   */
  useEffect(() => {
    if (debounceValue !== props?.initValue) {
      handleChange(debounceValue);
    }
  }, [debounceValue]);

  return (
    <Box className="controls">
      {/** Have to add the 'nodrag' className to prevent continuous
       * increment/decrement when clicking the arrow buttons on the
       * number input inside a ReactFlow node.
       * ReactFlow Docs: https://reactflow.dev/learn/customization/custom-nodes
       */}
      <input
        className="nodrag form-control form-control-6"
        data-testid={props["data-testid"]}
        type="number"
        step={incrementAmount}
        min={minValue}
        max={maxValue}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleChange(e.target.value)}
        disabled={props.enabled && props.editable ? "" : "disabled"}
        style={{
          width: "70px",
          backgroundColor:
            props?.enabled && props?.editable ? "#FFFFFF" : "#F0F0F0",
          textAlign: "center",
          height: props?.height,
        }}
        autoComplete={"off"}
      />
    </Box>
  );
};

IncrementingInput.propTypes = {
  initValue: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  enabled: PropTypes.bool.isRequired,
  editable: PropTypes.bool.isRequired,
  height: PropTypes.any,
  "data-testid": PropTypes.string,
};

IncrementingInput.defaultProps = {
  enabled: true,
  editable: false,
};
export const AddNewAutomationConfigButton = (props) => {
  const [isModalVisible, setIsModalVisible] = useState(false);

  return (
    <>
      <Button
        className="add-new-auto-config-button"
        data-testid={props.testId}
        data-attr="cap-ai:btn:add-new-automation-config"
        variant="contained"
        color="info"
        fullWidth
        startIcon={<AddIcon />}
        onClick={() => setIsModalVisible(true)}
        sx={{ textTransform: "none" }}
      >
        Add New
      </Button>
      <CreateAutomationConfigModal
        isVisible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        automationConfigTemplateId={props.automationConfigTemplateId}
      />
    </>
  );
};

AddNewAutomationConfigButton.propTypes = {
  automationConfigTemplateId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  testId: PropTypes.string,
};

// Exported configs are small JSON files (a few KB). Guard against a
// mistakenly-chosen huge file freezing the tab during read/parse.
const MAX_CONFIG_FILE_BYTES = 5 * 1024 * 1024;

export const ConfigJsonTransferButtons = (props) => {
  /**
   * Upload / Download buttons for moving an automation config between
   * environments as a JSON file. Download exports the selected config's
   * effective settings; Upload reads a previously downloaded file and opens
   * a modal (like Add New) to pick the new draft's name and model version —
   * the rule values come from the file, matched by field/label names on the
   * backend so files work across environments.
   */
  const fileInputRef = useRef(null);
  const [pendingImport, setPendingImport] = useState(null);

  const handleDownload = () => {
    downloadAutomationConfigJson(props.automationConfigId).catch((error) => {
      NucToast({
        message: `Config download failed: ${extractErrorDetail(error)}`,
        className: "autoflow-config-download-error-toast",
        type: "error",
      });
    });
  };

  const handleFileSelected = (file) => {
    if (!file) return;
    if (file.size > MAX_CONFIG_FILE_BYTES) {
      NucToast({
        message: `Config import failed: "${file.name}" is too large (max ${
          MAX_CONFIG_FILE_BYTES / (1024 * 1024)
        } MB)`,
        className: "autoflow-config-import-error-toast",
        type: "error",
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      let configJson;
      try {
        configJson = JSON.parse(evt.target.result);
      } catch {
        NucToast({
          message: `Config import failed: "${file.name}" is not valid JSON`,
          className: "autoflow-config-import-error-toast",
          type: "error",
        });
        return;
      }
      // Suggest a name from the file so the configs list shows where the
      // draft came from; the user can change it in the modal
      const fileStem = file.name
        .replace(/\.json$/i, "")
        .replace(/\.autoflow-config$/i, "");
      setPendingImport({
        configJson,
        defaultName: `${fileStem} (imported)`,
      });
    };
    reader.readAsText(file);
  };

  return (
    <div style={{ display: "flex", marginTop: "0.5em" }}>
      <CButton
        data-testid="cap-flow-btn-upload-config-json"
        data-attr="cap-ai:btn:upload-config-json"
        color="secondary"
        size="sm"
        block
        style={{ marginRight: "0.25em" }}
        onClick={() => fileInputRef.current.click()}
      >
        <CIcon content={freeSet.cilCloudUpload} /> Upload Config
      </CButton>
      <input
        type="file"
        accept=".json,application/json"
        style={{ display: "none" }}
        ref={fileInputRef}
        data-testid="cap-flow-input-config-json-file"
        onInput={(event) => {
          handleFileSelected(event.target.files[0]);
          event.target.value = "";
        }}
      />
      <CButton
        data-testid="cap-flow-btn-download-config-json"
        data-attr="cap-ai:btn:download-config-json"
        color="secondary"
        size="sm"
        block
        disabled={!props.automationConfigId}
        style={{ marginTop: 0, marginLeft: "0.25em" }}
        onClick={handleDownload}
      >
        <CIcon content={freeSet.cilCloudDownload} /> Download Config
      </CButton>
      <ImportAutomationConfigModal
        isVisible={!!pendingImport}
        onClose={() => setPendingImport(null)}
        automationConfigTemplateId={props.automationConfigTemplateId}
        configJson={pendingImport?.configJson}
        defaultName={pendingImport?.defaultName}
      />
    </div>
  );
};

ConfigJsonTransferButtons.propTypes = {
  automationConfigTemplateId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  automationConfigId: PropTypes.number,
};

export const ToggleText = (props) => {
  const [editable, setEditable] = useState(false);
  const [text, setText] = useState(props.text);

  const handleBlur = (event) => {
    if (event.target.value !== "") {
      props.handleNewText(event.target.value);
      setText(event.target.value);
    }
    setEditable(false);
  };

  return editable && props.enabled ? (
    <TextField
      defaultValue={text}
      placeholder={text}
      size="small"
      variant="outlined"
      autoFocus
      data-testid={props.testId}
      inputProps={{
        "data-testid": props.testId ? `${props.testId}-input` : undefined,
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.target.blur();
        }
      }}
      onBlur={handleBlur}
      sx={{
        "& .MuiOutlinedInput-root": {
          bgcolor: "action.hover",
          fontWeight: "bold",
        },
      }}
    />
  ) : (
    <Box
      component="span"
      onDoubleClick={() => setEditable(true)}
      data-testid={props.testId}
      sx={{ cursor: props.enabled ? "text" : "default" }}
    >
      {text}
    </Box>
  );
};

ToggleText.propTypes = {
  text: PropTypes.string.isRequired,
  enabled: PropTypes.bool.isRequired,
  handleNewText: PropTypes.func.isRequired,
  testId: PropTypes.string,
};

ToggleText.defaultProps = {
  enabled: true,
};

export const ConfirmButton = (props) => {
  //#TODO: pop a model and ask for confirmation
  const [text, setText] = useState("");
  console.log("Automation Config Template Button Group Props", props);
  return (
    <CDropdown>
      <CDropdownToggle
        className={`${props.command.toLowerCase()}-confirm-dropdown-toggle`}
        data-testid={props.testId}
        color={props.color}
        block={props.block}
        shape={props.shape}
      >
        {props.displayText}
      </CDropdownToggle>
      <CDropdownMenu>
        <CForm style={{ margin: "1em" }}>
          <CFormGroup>
            {props.message}
            <br></br>
            {props.showConfirm && (
              <>
                <CLabel htmlFor="exampleDropdownFormPassword1">
                  <p></p>
                  <h4>Type {props.command} to continue</h4>
                </CLabel>
                <CInput
                  data-testid={
                    props.testId ? `${props.testId}-input` : undefined
                  }
                  className={`form-control ${props.command.toLowerCase()}-confirm-input`}
                  onInput={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    } else {
                      setText(event.target.value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                />
              </>
            )}
          </CFormGroup>
          {props.showConfirm && (
            <>
              <CDropdownDivider />

              <CFormGroup className="mt-2 stretch">
                <CButton
                  className={`${props.command.toLowerCase()}-confirm-button`}
                  data-testid={
                    props.testId ? `${props.testId}-confirm` : undefined
                  }
                  color={props.color}
                  block={props.block}
                  shape={props.shape}
                  disabled={props.command !== text}
                  onClick={props.handleClick}
                >
                  {props.command}
                </CButton>
              </CFormGroup>
            </>
          )}
        </CForm>
      </CDropdownMenu>
    </CDropdown>
  );
};

ConfirmButton.propTypes = {
  message: PropTypes.string.isRequired,
  displayText: PropTypes.string.isRequired,
  command: PropTypes.string.isRequired,
  handleClick: PropTypes.func.isRequired,
  showConfirm: PropTypes.bool.isRequired,
  color: PropTypes.string.isRequired,
  shape: PropTypes.string.isRequired,
  block: PropTypes.bool.isRequired,
  testId: PropTypes.string,
};

ConfirmButton.defaultProps = {
  showConfirm: false,
};

export const AutomationConfigTemplateHeader = (props) => {
  // `selectedButton` values ("overview" / "autoflow" / "kpis" / "audit" /
  // "integrate") line up 1:1 with TemplateTabs `activeValue`.
  // `cap-tmpl-header-${id}` is emitted by TemplatePageHeader itself,
  // so no outer wrapper needed.
  return (
    <>
      <TemplatePageHeader
        templateId={props.automationConfigTemplateId}
        pageLabel={props.title}
        testIdPrefix={props.testIdPrefix}
      />
      {props.selectedButton && (
        <TemplateTabs
          templateId={props.automationConfigTemplateId}
          activeValue={props.selectedButton}
        />
      )}
    </>
  );
};

AutomationConfigTemplateHeader.propTypes = {
  automationConfigTemplateId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  title: PropTypes.string,
  selectedButton: PropTypes.string,
  testIdPrefix: PropTypes.string,
};

AutomationConfigTemplateHeader.defaultProps = {
  title: "Automation Template",
  selectedButton: null,
  testIdPrefix: "cap-tmpl",
};

export const TestRunModal = (props) => {
  const [testRunName, setTestRunName] = useState("");
  const [tempWhereFilters, setTempWhereFilters] = useState([
    initialWhereFilterValue,
  ]);
  const [whereFilters, setWhereFilters] = useState([]);
  const [datasetId, setDatasetId] = useState(
    props.automationConfigTemplate?.ground_truth_dataset_fk,
  );
  const [selectedTestRun, setSelectedTestRun] = useState(null);
  const [areFiltersShowing, setAreFiltersShowing] = useState(false);
  const { data: modelDatasets } = useModelVersionDatasets(
    props.automationConfig?.model_version?.model_fk,
    props.automationConfig?.model_version?.version_number,
  );
  const { data: testRuns } = useGetTestRunByAutomationConfigTemplateId(
    props.automationConfigTemplate?.id,
  );
  const { data: datasetFields } = useDatasetFields(datasetId);

  const resetFilters = () => {
    setTempWhereFilters([initialWhereFilterValue]);
    setWhereFilters([]);
  };

  const resetModalState = () => {
    setWhereFilters([]);
    setTestRunName("");
    setTempWhereFilters([initialWhereFilterValue]);
    setDatasetId(props.automationConfigTemplate?.ground_truth_dataset_fk);
    setSelectedTestRun(null);
    setAreFiltersShowing(false);
    props.setTestRunId(-1);
  };

  useEffect(() => {
    const testRun = testRuns?.find(
      (run) => run.test_run.id === Number(props.testRunId),
    );
    setSelectedTestRun(testRun);
    if (Number(props.testRunId) === -1) {
      setTestRunName("");
      setDatasetId("");
    } else {
      setAreFiltersShowing(false);
      setTestRunName(testRun.test_run.name);
      setDatasetId(testRun.dataset.id);
    }
  }, [props.testRunId]);

  useEffect(() => {
    if (!props.canCreateNewTestRun && testRuns && testRuns.length > 0) {
      props.setTestRunId(testRuns[0].test_run.id);
    }
  }, [testRuns]);

  return (
    <CModal show={props.isVisible} onClose={props.onClose}>
      <CModalHeader>
        <h3>Run Calibration Automation Stats</h3>
      </CModalHeader>
      <CModalBody style={{ textAlign: "left" }}>
        <CForm>
          <CFormGroup row>
            <CLabel className="col-sm-3 col-form-label">
              {props.canCreateNewTestRun
                ? "Previous Test Run (Optional)"
                : "Test Run"}
            </CLabel>
            <CCol sm={8}>
              <CSelect
                data-testid="cap-flow-select-test-run"
                data-attr="cap-ai:select:test-run"
                value={props.testRunId}
                onChange={(e) => props.setTestRunId(e.target.value)}
              >
                <option key={-1} value={-1}>
                  Select a Test Run
                </option>
                {testRuns?.map((item) => (
                  <option key={item.test_run.id} value={item.test_run.id}>
                    {item.test_run.name}
                  </option>
                ))}
              </CSelect>
            </CCol>
          </CFormGroup>
          {Number(props.testRunId) !== -1 && (
            <>
              <CFormGroup row>
                <CLabel className="col-sm-3 col-form-label">Start Date:</CLabel>
                <CCol sm={8}>
                  {new Date(
                    Date.parse(selectedTestRun?.test_run.create_date),
                  ).toLocaleString("en-US", {
                    hour12: false,
                  })}
                </CCol>
              </CFormGroup>
              <CFormGroup row>
                <CLabel className="col-sm-3 col-form-label">End Date:</CLabel>
                <CCol sm={8}>
                  {new Date(
                    Date.parse(selectedTestRun?.test_run.end_date),
                  ).toLocaleString("en-US", {
                    hour12: false,
                  })}
                </CCol>
              </CFormGroup>
              <CFormGroup row>
                <CLabel className="col-sm-3 col-form-label">Count:</CLabel>
                <CCol sm={8}>{selectedTestRun?.count}</CCol>
              </CFormGroup>
            </>
          )}
          <CFormGroup row>
            <CLabel className="col-sm-3 col-form-label">Name</CLabel>
            <CCol sm={8}>
              <CInput
                id="test-run-name-input"
                data-attr="cap-ai:input:test-run-name"
                value={testRunName}
                onChange={(e) => setTestRunName(e.target.value)}
                disabled={
                  Number(props.testRunId) !== -1 || !props.canCreateNewTestRun
                }
              ></CInput>
            </CCol>
          </CFormGroup>
          <CFormGroup row>
            <CLabel className="col-sm-3 col-form-label">Dataset</CLabel>
            <CCol sm={8}>
              <ListSelect
                id="run-calibration-dataset-select"
                selectedValue={datasetId}
                handleSelectedValue={setDatasetId}
                value={datasetId}
                items={modelDatasets}
                disabled={
                  !modelDatasets ||
                  Number(props.testRunId) !== -1 ||
                  !props.canCreateNewTestRun
                }
              />
            </CCol>
          </CFormGroup>
          {props.canCreateNewTestRun ? (
            <>
              <CLabel
                htmlFor="optional-filters-switch"
                style={{ marginRight: "10px", float: "left" }}
              >
                <i>Optional Dataset Filters</i>
              </CLabel>
              <CSwitch
                style={{ float: "left" }}
                id="optional-filters-switch"
                data-attr="cap-ai:toggle:optional-dataset-filters"
                color={"info"}
                size={"sm"}
                checked={areFiltersShowing}
                onChange={(e) => {
                  setAreFiltersShowing(e.target.checked);
                  setWhereFilters([]);
                }}
                disabled={
                  Number(props.testRunId) !== -1 || !props.canCreateNewTestRun
                }
              />
              {areFiltersShowing && (
                <RecordFilter
                  whereFilters={whereFilters}
                  setWhereFilters={setWhereFilters}
                  id={datasetId}
                  group="dataset"
                  fields={datasetFields}
                  tempWhereFilters={tempWhereFilters}
                  setTempWhereFilters={setTempWhereFilters}
                  hideApplyButton={true}
                  resetFilters={resetFilters}
                />
              )}
            </>
          ) : (
            <></>
          )}
        </CForm>
      </CModalBody>
      <CModalFooter>
        <CCol sm="12" style={{ textAlign: "right" }}>
          <CButton
            id="submit-update-new-automation-template-button"
            data-testid="cap-flow-btn-run-test"
            data-attr="cap-ai:btn:run-calibration-test"
            color="success"
            disabled={
              ((areFiltersShowing && !areWhereFiltersValid(whereFilters)) ||
                datasetId === "" ||
                testRunName.replace(/ /g, "") === "") && // check that name isnt blank
              !(Number(props.testRunId) !== -1)
            }
            onClick={() => {
              props.onSubmit({
                name: testRunName,
                datasetId: datasetId,
                automationConfigTemplateId: props.automationConfigTemplate?.id,
                modelVersionId: props.automationConfig?.model_version?.id,
                whereFilters: whereFilters,
                testRunId: props.testRunId,
              });
              resetModalState();
              props.onClose();
            }}
          >
            Run Test
          </CButton>
        </CCol>
      </CModalFooter>
    </CModal>
  );
};

TestRunModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  automationConfigTemplate: PropTypes.object.isRequired,
  automationConfig: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  canCreateNewTestRun: PropTypes.bool.isRequired,
  setTestRunId: PropTypes.func.isRequired,
  testRunId: PropTypes.number.isRequired,
};
